import { contrastRatio, luminance, parseHex } from './contrast'

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
const toGamma = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055)

function toOklab([r, g, b]: [number, number, number]): [number, number, number] {
  const [lr, lg, lb] = [r, g, b].map(c => toLinear(c / 255))
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb)
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb)
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb)
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ]
}

function fromOklab([L, a, b]: [number, number, number]): [number, number, number] {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3
  const linear = [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ]
  const [r, g, bl] = linear.map(c => Math.round(Math.min(1, Math.max(0, toGamma(c))) * 255))
  return [r, g, bl]
}

/**
 * `share` de `color` dans `base`, mêlés en oklab — le calcul de
 * `color-mix(in oklab, color share, base)` en CSS. Sert à vérifier en test ce
 * que le navigateur peindra, et à dériver des teintes côté JS (#79).
 */
export function mixOklab(color: string, base: string, share: number): string | null {
  const a = parseHex(color)
  const b = parseHex(base)
  if (!a || !b) return null
  const [la, lb] = [toOklab(a), toOklab(b)]
  const mixed = fromOklab([0, 1, 2].map(i => la[i] * share + lb[i] * (1 - share)) as [number, number, number])
  return `#${mixed.map(c => c.toString(16).padStart(2, '0')).join('')}`.toUpperCase()
}

/**
 * Fond de l'item actif et du survol dans la barre latérale, posée sur la
 * primaire (#79). Il doit se distinguer de la primaire sans que l'encre y
 * perde AA.
 *
 * Quand l'écart primaire / encre est large (marque très sombre ou très claire),
 * on glisse vers l'encre : c'est ce qui se voit. Quand il est juste — un violet
 * ou un bleu de milieu de gamme sous du blanc —, glisser vers l'encre fait
 * passer sous AA dès 10 % ; on s'en éloigne alors, vers le noir ou le blanc
 * opposé.
 */
export function deriveSidebarAccent(primary: string, ink: string): string | null {
  const ratio = contrastRatio(primary, ink)
  const inkRgb = parseHex(ink)
  if (ratio == null || !inkRgb) return null
  const opposite = luminance(inkRgb) > 0.5 ? '#000000' : '#FFFFFF'
  return ratio >= 7 ? mixOklab(ink, primary, 0.12) : mixOklab(opposite, primary, 0.18)
}
